import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { AUTH_STALE_EVENT, api, type User } from './api';

interface AuthState {
  user: User | null;
  /** True until the initial /auth/me check resolves — routes must wait for this. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      api<{ user: User | null }>('/auth/me')
        .then((r) => !cancelled && setUser(r.user))
        .catch(() => !cancelled && setUser(null))
        .finally(() => !cancelled && setLoading(false));

    void refresh();

    /*
      Role and permissions are read from the database on every request, so the
      server always has the current picture — but this copy would otherwise
      stay as it was at page load. Re-read it when the server refuses something
      (a grant withdrawn under our feet) and when the tab comes back into view.
    */
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener(AUTH_STALE_EVENT, refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_STALE_EVENT, refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { user: signedIn } = await api<{ user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setUser(signedIn);
  }, []);

  const signOut = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut }),
    [user, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
